import { amplitude } from "@eis-poc/lib-discover-medical-mfes";

export const EVENTS = {
  searchInitiated: "Search - initiated",
  searchFeedback: "Search Feedback - clicked",
  videoViewed: "Video - viewed",
  videoPlayed: "Video - played",
  imageViewed: "Image - viewed",
  algorithmViewed: "Algorithm - viewed",
  moreResults: "Search Results - more_clicked",
  searchResultsToolClicked: "Search Results - tool_clicked",
  searchResultsTopicClicked: "Search Results - topic_clicked",
  searchResultsViewedMain: "Search Results - viewed",
  searchResultsViewedVideos: "Video Search Results - viewed",
  searchResultsViewedImages: "Image Search Results - viewed",
  searchResultsViewedPH: "Patient Handout Search Results - viewed",
  searchResultsViewedAlgorithms: "Algorithm Search Results - viewed",
  searchResultsViewedLeadership: "Leadership Search Results - viewed",
  ragSearchInitiated: "RAG Search - initiated",
  ragSearchResultViewed: "RAG Search Result - viewed",
  ragSearchResultReferenceClicked: "RAG Search Result - reference_clicked",
  ragSearchFeedbackSubmitted: "RAG Search Feedback - submitted",
  ragSearchResultDismissed: "RAG Search Result - dismissed",
  ragSettingsUpdated: "RAG Settings - updated",
  ragSearchError: "RAG Search - error",
  guidedQuestionsListClicked: "Guided Questions - list_clicked",
  guidedQuestionsTopicClicked: "Guided Questions - topic_clicked",
  searchPlacardClicked: "Search Placard - clicked",
  boxedWarningClicked: "Boxed Warning - clicked",
  drugMerativeLinkClicked: "Drug Merative Link - clicked",
  checkInteractionsClicked: "Check Interactions - clicked",
  decisionsPlacardClicked: "Decisions Placard - clicked",
  aboutCollectionsClicked: "About Collections Dialog - clicked",
  associatedCollectionClicked: "Associated Collection - clicked",
  calculatorClicked: "Calculator - clicked",
  tableClicked: "Table - clicked",
};

export const AFFORDANCE = {
  goTo: "go-to",
  recentlyViewed: "recently-viewed",
  searchFor: "search-for",
  manual: "manual",
  filter: "filter",
  title: "title",
  subtitle: "subtitle",
  toc: "toc",
  resultsViewed: "result-list",
  expand: "expand",
  collapse: "collapse",
  searchGallery: "search-gallery",
  searchModal: "search-modal",
  resultVIC: "view-in-context-result",
  modalVIC: "view-in-context-modal",
  exactMatchDrugsPlacard: "exact-match-drugs",
  searchRightrail: "search-rightrail",
  searchBottom: "search-bottom",
  drugExactMatch: "drugExactMatch",
  sourcesUsed: "sources-used",
  workplaceSkill: "workplace-skill",
  recommendedContent: "recommended-content",
  recommendedQuestion: "recommended-question",
  quickFeedback: "quick-feedback",
  aboutClinicalCollection: "about-clinical-collection",
};

export const NAVIGATION = {
  neonatal: "NeoFax",
  pediatric: "Pediatrics",
  searchResults: "Search Results",
};

const getSearchTerms = (searchTerm = "", searchTermMisspelled = "") =>
  searchTermMisspelled
    ? {
        search_term: searchTerm,
        search_term_misspelled: searchTermMisspelled,
      }
    : { search_term: searchTerm };

const getTabViewedEventName = (tabName) => {
  const map = {
    main: EVENTS.searchResultsViewedMain,
    videos: EVENTS.searchResultsViewedVideos,
    images: EVENTS.searchResultsViewedImages,
    "patient-handouts": EVENTS.searchResultsViewedPH,
    algorithms: EVENTS.searchResultsViewedAlgorithms,
    leadership: EVENTS.searchResultsViewedLeadership,
  };

  return map[tabName] || map.main;
};

const getFilterEventProperties = ({
  contentTypeFilterValue,
  contentFilterValue,
  populationFilterValue,
  specialityFilterValue,
  patientHandoutFilterValue,
  types,
  specialities,
  populations,
  phtypes,
}) => {
  const filterEventProperties = {};
  contentTypeFilterValue =
    contentTypeFilterValue || contentFilterValue || types;
  populationFilterValue = populationFilterValue || populations;
  specialityFilterValue = specialityFilterValue || specialities;
  patientHandoutFilterValue = patientHandoutFilterValue || phtypes;
  if (contentTypeFilterValue) {
    filterEventProperties.content_type_filter = contentTypeFilterValue;
  }
  if (populationFilterValue) {
    filterEventProperties.patient_population_type_filter = populationFilterValue;
  }
  if (specialityFilterValue) {
    filterEventProperties.specialties_filter = specialityFilterValue;
  }
  if (patientHandoutFilterValue) {
    filterEventProperties.patient_handout_type_filter = patientHandoutFilterValue;
  }
  return filterEventProperties;
};

export const logSearchInitiated = (telemetryData) => {
  const { affordance, searchTerm, language, hl7 } = telemetryData;
  const eventProperties = {
    affordance,
    search_term: searchTerm,
    language,
    hl7,
  };
  const filterEventProperties = getFilterEventProperties(telemetryData);

  amplitude.log(EVENTS.searchInitiated, {
    ...eventProperties,
    ...filterEventProperties,
  });
};

export const logImageViewed = (telemetryData) => {
  const { affordance, title, id, format } = telemetryData;
  const eventProperties = {
    title,
    media_id: id,
    type: format,
    affordance,
  };
  amplitude.log(EVENTS.imageViewed, eventProperties);
};

export const logAlgorithmViewed = (telemetryData) => {
  const { affordance, title, id, format, mediaId } = telemetryData;
  const eventProperties = {
    title,
    media_id: id ?? mediaId,
    type: format,
    affordance,
  };
  amplitude.log(EVENTS.algorithmViewed, eventProperties);
};

export const logVideoViewed = (telemetryData) => {
  const { affordance, title, id, format } = telemetryData;
  const eventProperties = {
    title,
    media_id: id,
    type: format,
    affordance,
  };
  amplitude.log(EVENTS.videoViewed, eventProperties);
};

export const logVideoPlayed = (telemetryData) => {
  const { affordance, title, id, format } = telemetryData;
  const eventProperties = {
    title,
    media_id: id,
    type: format,
    affordance,
  };
  amplitude.log(EVENTS.videoPlayed, eventProperties);
};

export const logSearchFeedback = (telemetryData) => {
  const {
    feedback,
    searchTerm,
    pageToken,
    searchTimeInMs,
    articlesReturned,
  } = telemetryData;
  const eventProperties = {
    feedback,
    search_term: searchTerm ?? "",
    result_set_id: pageToken,
    results: articlesReturned,
    search_duration: searchTimeInMs,
  };
  amplitude.log(EVENTS.searchFeedback, eventProperties);
};
export const logMoreResults = (telemetryData) => {
  const {
    affordance,
    searchTerm,
    workflowId,
    pageToken,
    page,
    articlesReturned,
    hl7,
  } = telemetryData;
  const eventProperties = {
    affordance,
    search_term: searchTerm ?? "",
    workflow_id: workflowId,
    result_set_id: pageToken,
    offset: (page - 1) * 10 + 1,
    articles_returned: articlesReturned,
    hl7,
  };
  amplitude.log(EVENTS.moreResults, eventProperties);
};

const buildV2PlacardTelemetryData = (telemetryData) => {
  const { placardCount, placardTypes = [] } = telemetryData;
  const placardCountsByType = placardTypes.reduce((acc, placardType) => {
    const capitalCasedPlacardType =
      placardType.charAt(0).toUpperCase() + placardType.slice(1);
    acc[`placard_${placardType.toLowerCase()}_count`] =
      telemetryData[`placard${capitalCasedPlacardType}Count`] ?? 0;
    return acc;
  }, {});
  return {
    placard_count: placardCount,
    ...placardCountsByType,
  };
};

export const logSearchResultsViewed = (telemetryData) => {
  const {
    searchTerm,
    searchTermMisspelled,
    workflowId,
    pageToken,
    articlesReturned,
    resultCount,
    imageCount,
    videoCount,
    peCount,
    algorithmCount,
    dsdToolCount,
    searchTimeInMs,
    hl7,
    segment,
    language,
    collectionTitle,
  } = telemetryData;
  const searchTerms = getSearchTerms(searchTerm, searchTermMisspelled);
  const filterEventProperties = getFilterEventProperties(telemetryData);
  const eventName = getTabViewedEventName(segment);
  const placardTelemetryData = buildV2PlacardTelemetryData(telemetryData);

  const eventProperties = {
    affordance: AFFORDANCE.resultsViewed,
    segment: segment,
    workflow_id: workflowId,
    result_set_id: pageToken,
    articles_returned: articlesReturned,
    result_count: resultCount,
    image_count: imageCount,
    video_count: videoCount,
    algorithm_count: algorithmCount,
    pe_count: peCount,
    tool_count: dsdToolCount,
    search_duration: searchTimeInMs,
    hl7,
    language,
    title: collectionTitle,
    ...placardTelemetryData,
    ...searchTerms,
    ...filterEventProperties,
  };
  amplitude.log(eventName, eventProperties);
};

export const logSearchResultsTopicClicked = (telemetryData) => {
  const {
    affordance,
    segment,
    searchTerm,
    position,
    title,
    rrScore,
    sectionLevel,
    sectionPosition,
    pubType,
    recordAn,
    navigateTo,
    articlesReturned,
    pageToken,
    workflowId,
    id,
    mediaTitle,
  } = telemetryData;

  const eventProperties = {
    affordance,
    segment: segment,
    search_term: searchTerm ?? "",
    workflow_id: workflowId,
    result_set_id: pageToken,
    position,
    title,
    navigate_to_media_id: id,
    navigate_to_media_title: mediaTitle,
    rr_score: rrScore,
    section_level: sectionLevel,
    section_position: sectionPosition,
    pub_type: pubType,
    record_an: recordAn,
    articles_returned: articlesReturned,
    navigate_to: navigateTo,
  };
  amplitude.log(EVENTS.searchResultsTopicClicked, eventProperties);
};

export const logSearchResultsToolClicked = (telemetryData) => {
  const { id, rrScore, title } = telemetryData;

  const eventProperties = {
    id,
    rr_score: rrScore,
    title,
  };
  amplitude.log(EVENTS.searchResultsToolClicked, eventProperties);
};

export const logGuidedQuestionsListClicked = (telemetryData) => {
  const { searchTerm, question, userAction } = telemetryData;

  const eventProperties = {
    affordance: userAction,
    search_term: searchTerm,
    question,
  };
  amplitude.log(EVENTS.guidedQuestionsListClicked, eventProperties);
};

export const logGuidedQuestionsTopicClicked = (telemetryData) => {
  const { searchTerm, question, slug, title, rrScore } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question,
    slug,
    title,
    rr_score: rrScore,
  };
  amplitude.log(EVENTS.guidedQuestionsTopicClicked, eventProperties);
};

export const logSearchPlacardClicked = (telemetryData) => {
  const {
    placardType,
    searchTerm,
    pubType,
    title,
    groupTitle,
    recordAn,
    navigateTo,
  } = telemetryData;

  const eventProperties = {
    placard_type: placardType,
    search_term: searchTerm,
    pub_type: pubType,
    title,
    group_title: groupTitle ?? "",
    record_an: recordAn,
    navigate_to: navigateTo,
  };
  amplitude.log(EVENTS.searchPlacardClicked, eventProperties);
};

export const logBoxedWarningClicked = (telemetryData) => {
  const {
    affordance,
    searchTerm,
    pubType,
    title,
    recordAn,
    groupTitle,
  } = telemetryData;

  const eventProperties = {
    affordance,
    search_term: searchTerm ?? "",
    pub_type: pubType,
    title,
    group_title: groupTitle ?? "",
    record_an: recordAn,
  };
  amplitude.log(EVENTS.boxedWarningClicked, eventProperties);
};

export const logDrugMerativeLinkClicked = (telemetryData) => {
  const {
    affordance,
    title,
    navigateTo,
    navigateFrom,
    recordAn,
  } = telemetryData;

  const eventProperties = {
    affordance,
    title,
    record_an: recordAn,
    navigate_to: NAVIGATION[navigateTo.toLowerCase()],
    navigate_from: navigateFrom,
  };
  amplitude.log(EVENTS.drugMerativeLinkClicked, eventProperties);
};

export const logCheckInteractionsClicked = (telemetryData) => {
  const {
    affordance,
    searchTerm,
    pubType,
    title,
    recordAn,
    groupTitle,
  } = telemetryData;

  const eventProperties = {
    affordance,
    search_term: searchTerm ?? "",
    pub_type: pubType,
    title,
    group_title: groupTitle ?? "",
    record_an: recordAn,
  };
  amplitude.log(EVENTS.checkInteractionsClicked, eventProperties);
};

export const logDecisionsPlacardClicked = (telemetryData) => {
  const { affordance, id, title, searchResults, searchTerm } = telemetryData;

  const eventProperties = {
    affordance,
    tool_an: id,
    tool_title: title,
    search_results: searchResults,
    search_term: searchTerm,
  };
  amplitude.log(EVENTS.decisionsPlacardClicked, eventProperties);
};

export const logRagSearchInitiated = (telemetryData) => {
  const { searchTerm, affordance } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    affordance,
  };
  amplitude.log(EVENTS.ragSearchInitiated, eventProperties);
};

export const logRagSearchResultViewed = (telemetryData) => {
  const {
    searchTerm,
    question,
    pageToken,
    searchTimeInMs,
    userIntentTimeInMs,
    ragResponseTimeInMs,
    userIntentCategory,
    answerNotFound,
    error,
  } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question: question,
    search_duration: searchTimeInMs,
    user_intent_duration: userIntentTimeInMs,
    rag_response_duration: ragResponseTimeInMs,
    user_intent_category: userIntentCategory,
    result_set_id: pageToken,
    answer_not_found: answerNotFound,
    error,
  };
  amplitude.log(EVENTS.ragSearchResultViewed, eventProperties);
};

export const logRagSearchReferenceClicked = (telemetryData) => {
  const {
    searchTerm,
    question,
    pageToken,
    searchTimeInMs,
    title,
    navigateTo,
    affordance,
    recordAn,
  } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question: question,
    search_duration: searchTimeInMs,
    result_set_id: pageToken,
    title,
    navigate_to: navigateTo,
    affordance,
    record_an: recordAn,
  };
  amplitude.log(EVENTS.ragSearchResultReferenceClicked, eventProperties);
};

export const logRagSearchFeedbackSubmitted = (telemetryData) => {
  const {
    searchTerm,
    question,
    pageToken,
    userIntentCategory,
    overallRating,
    concerns,
    informationLevel,
    acceptableUse,
    accuracy,
    message,
    affordance,
    answerNotFound,
    type,
  } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question: question,
    result_set_id: pageToken,
    user_intent_category: userIntentCategory,
    overall_rating: overallRating,
    equity_concern: concerns,
    information_level: informationLevel,
    acceptable_use: acceptableUse,
    accuracy,
    message,
    affordance,
    answer_not_found: answerNotFound,
    type,
  };
  amplitude.log(EVENTS.ragSearchFeedbackSubmitted, eventProperties);
};

export const logRagSearchError = (telemetryData) => {
  const { searchTerm, question } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question,
  };
  amplitude.log(EVENTS.ragSearchError, eventProperties);
};

export const logRagSearchResultDismissed = (telemetryData) => {
  const {
    searchTerm,
    question,
    pageToken,
    searchTimeInMs,
    userIntentTimeInMs,
    ragResponseTimeInMs,
    answerNotFound,
    error,
  } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    question: question,
    search_duration: searchTimeInMs,
    user_intent_duration: userIntentTimeInMs,
    rag_response_duration: ragResponseTimeInMs,
    result_set_id: pageToken,
    answer_not_found: answerNotFound,
    error,
  };
  amplitude.log(EVENTS.ragSearchResultDismissed, eventProperties);
};

export const logRagSettingsUpdated = (telemtryData) => {
  const { show } = telemtryData;

  const eventProperties = {
    show,
  };

  amplitude.log(EVENTS.ragSettingsUpdated, eventProperties);
};

export const logAboutCollectionsDialogClicked = (telemetryData) => {
  const { searchTerm, affordance, recordAn, title } = telemetryData;

  const eventProperties = {
    search_term: searchTerm,
    affordance,
    record_an: recordAn,
    title,
  };
  amplitude.log(EVENTS.aboutCollectionsClicked, eventProperties);
};

export const logAssociatedCollectionClicked = (telemetryData) => {
  const { placardFrom, placardTo } = telemetryData;
  const eventProperties = {
    placard_from: placardFrom,
    placard_to: placardTo,
    placard_type: "clinicalCollection",
  };
  amplitude.log(EVENTS.associatedCollectionClicked, eventProperties);
};

export const logCalculatorClicked = (telemetryData) => {
  const { affordance, calculatorTitle, affordanceTitle } = telemetryData;
  const eventProperties = {
    affordance,
    calculator_title: calculatorTitle,
    affordance_title: affordanceTitle,
  };
  amplitude.log(EVENTS.calculatorClicked, eventProperties);
};

export const logTableLinkClicked = (telemetryData) => {
  const {
    searchTerm,
    title,
    recordAn,
    navigateFrom,
    tableId,
    tableTitle,
  } = telemetryData;
  const eventProperties = {
    search_term: searchTerm,
    title,
    record_an: recordAn,
    table_title: tableTitle,
    table_id: tableId,
    navigate_from: navigateFrom,
  };
  amplitude.log(EVENTS.tableClicked, eventProperties);
};
